"use client";

import { Suspense, useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import type { CaptureResult } from "posthog-js";
import {
  getAnalyticsBrandProperties,
  readBrandUtmStorage,
  resolveAnalyticsBrandConfig,
  shouldCaptureAnalytics,
  writeBrandUtmStorage,
  type AnalyticsBrandConfig,
} from "@/lib/analytics-brand-config";

// Same-origin reverse proxy (see next.config.ts rewrites) so ad blockers can't
// drop ingestion requests. Override only if the proxy is unavailable.
const POSTHOG_HOST = process.env.NEXT_PUBLIC_POSTHOG_INGEST_HOST || "/ingest";
const POSTHOG_UI_HOST = "https://us.posthog.com";

type PostHogClient = typeof import("posthog-js").default;
type PostHogRuntimeEnvironment = "production" | "local" | "preview" | "other";
type WebVitalName = "CLS" | "FCP" | "INP" | "LCP";
type WebVitalRating = "good" | "needs-improvement" | "poor";

type PendingPostHogIdentify = {
  userId: string;
  properties?: Record<string, unknown>;
};

type PendingPostHogEvent = {
  eventName: string;
  properties?: Record<string, unknown>;
};

type WebVitalMetric = {
  name: WebVitalName;
  value: number;
  rating: WebVitalRating;
  id: string;
  delta: number;
  navigationType: string;
  source: "direct_performance_observer";
  timestamp: number;
  attribution?: Record<string, string | number | boolean | null>;
};

type LayoutShiftEntry = PerformanceEntry & {
  value?: number;
  hadRecentInput?: boolean;
};

type LargestContentfulPaintEntry = PerformanceEntry & {
  element?: Element;
  loadTime?: number;
  renderTime?: number;
  size?: number;
};

type EventTimingEntry = PerformanceEntry & {
  interactionId?: number;
  processingEnd?: number;
  processingStart?: number;
};

type PostHogRuntimeState = {
  initialized: boolean;
  initializing: boolean;
  listenersAttached: boolean;
  webVitalsAttached: boolean;
  idleHandle: number | null;
  timeoutHandle: ReturnType<typeof setTimeout> | null;
  webVitalsFlushHandle: ReturnType<typeof setTimeout> | null;
  pendingIdentifies: PendingPostHogIdentify[];
  pendingEvents: PendingPostHogEvent[];
  pendingWebVitals: Partial<Record<WebVitalName, WebVitalMetric>>;
  reportedWebVitals: WebVitalName[];
  webVitalsObservers: PerformanceObserver[];
  resetQueued: boolean;
  adminCaptureSuppressed: boolean;
};

type PostHogCapturePayload = CaptureResult & {
  properties?: Record<string, unknown>;
};

const POSTHOG_STATE_KEY = "__tuscanCovePostHogState";
const MAX_PENDING_IDENTIFIES = 10;
let activeBrandConfig: AnalyticsBrandConfig | null = null;
const MAX_PENDING_EVENTS = 50;
const PASSIVE_INIT_DELAY_MS = 1000;
const STATIC_COMPLIANCE_INIT_DELAY_MS = 5000;
const PASSIVE_INIT_IDLE_TIMEOUT_MS = 2500;
const WEB_VITALS_FLUSH_DELAY_MS = 5000;
const WEB_VITAL_NAMES: WebVitalName[] = ["CLS", "FCP", "INP", "LCP"];
let posthogClient: PostHogClient | null = null;
let posthogClientPromise: Promise<PostHogClient> | null = null;
const webVitalsPageId = `tcwv_${Date.now()}_${Math.random().toString(16).slice(2)}`;

function getActiveBrandConfig(): AnalyticsBrandConfig | null {
  if (typeof window === "undefined") return null;
  return activeBrandConfig ?? resolveAnalyticsBrandConfig();
}

function normalizePostHogPathname(pathname: string | null | undefined) {
  const normalized = (pathname || "/").split("?")[0]?.split("#")[0]?.replace(/\/+$/, "") ?? "";
  return normalized || "/";
}

function isAdminAnalyticsPath(pathname: string | null | undefined) {
  const normalized = normalizePostHogPathname(pathname);
  return normalized === "/admin" || normalized.startsWith("/admin/");
}

function getCurrentPostHogPathname() {
  if (typeof window === "undefined") return undefined;
  return window.location.pathname;
}

function isStaticCompliancePath(pathname: string | null | undefined) {
  const normalized = normalizePostHogPathname(pathname);
  return (
    normalized === "/accessibility" ||
    normalized === "/privacy" ||
    normalized === "/terms"
  );
}

function getPostHogDeviceCategory() {
  if (typeof window === "undefined") return undefined;

  const userAgent = window.navigator.userAgent;
  const width = window.innerWidth;
  if (/iPad|Tablet|Android(?!.*Mobile)/i.test(userAgent) || (width >= 768 && width < 1024)) {
    return "tablet";
  }

  if (/Mobi|iPhone|iPod|Android/i.test(userAgent) || width < 768) {
    return "mobile";
  }

  return "desktop";
}

function getPostHogBrowserContext() {
  if (typeof window === "undefined") return {};

  return {
    device_category: getPostHogDeviceCategory(),
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}

function getPostHogRuntimeEnvironment(): PostHogRuntimeEnvironment {
  const brandConfig = getActiveBrandConfig();
  if (!brandConfig) return "other";
  if (brandConfig.isProductionHost) return "production";
  if (brandConfig.hostname === "localhost" || brandConfig.hostname === "127.0.0.1") {
    return "local";
  }
  if (brandConfig.hostname.endsWith(".vercel.app")) return "preview";
  return "other";
}

function shouldSkipPostHog() {
  if (typeof window === "undefined") return true;
  if (isAdminAnalyticsPath(getCurrentPostHogPathname())) return true;

  const brandConfig = getActiveBrandConfig();
  if (!brandConfig?.posthogKey) return true;
  return !shouldCaptureAnalytics(brandConfig.hostname);
}

function shouldCaptureProductionWebVitals() {
  const brandConfig = getActiveBrandConfig();
  return !shouldSkipPostHog() && Boolean(brandConfig?.isProductionHost);
}

export function getPostHogDistinctId() {
  const brandConfig = getActiveBrandConfig();
  const storageKey =
    brandConfig?.storage.posthogDistinctId ?? "tuscan_cove_posthog_distinct_id";
  const idPrefix = brandConfig?.brandId === "roma-kitchen" ? "roma" : "tc";

  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) return stored;

    const nextId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${idPrefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    localStorage.setItem(storageKey, nextId);
    return nextId;
  } catch {
    return `${idPrefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  }
}

function sendDirectPostHogCapture(eventName: string, properties?: Record<string, unknown>) {
  if (shouldSkipPostHog()) return;
  const brandConfig = getActiveBrandConfig();
  const posthogKey = brandConfig?.posthogKey;
  if (!posthogKey || !brandConfig) return;

  const distinctId = getPostHogDistinctId();
  const payload = {
    api_key: posthogKey,
    event: eventName,
    distinct_id: distinctId,
    properties: {
      ...getAnalyticsBrandProperties(brandConfig),
      platform: "web",
      environment: getPostHogRuntimeEnvironment(),
      $lib: "tuscan_direct_capture",
      $raw_user_agent: window.navigator.userAgent,
      $host: window.location.host,
      $pathname: window.location.pathname,
      $current_url: window.location.href,
      $referrer: document.referrer || undefined,
      posthog_distinct_id: distinctId,
      title: document.title,
      ...getPostHogBrowserContext(),
      ...properties,
    },
  };

  try {
    void fetch(`${POSTHOG_HOST.replace(/\/$/, "")}/capture/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {
      // Analytics ingestion can fail because of blockers/network limits; do not report it as an app exception.
    });
  } catch {
    // Analytics must never break the user experience.
  }
}

function loadPostHogClient() {
  if (posthogClient) return Promise.resolve(posthogClient);

  posthogClientPromise ??= import("posthog-js").then((module) => {
    posthogClient = module.default;
    return posthogClient;
  });

  return posthogClientPromise;
}

function getPostHogRuntimeState(): PostHogRuntimeState {
  const win = window as typeof window & {
    [POSTHOG_STATE_KEY]?: PostHogRuntimeState;
  };

  win[POSTHOG_STATE_KEY] ??= {
    initialized: false,
    initializing: false,
    listenersAttached: false,
    webVitalsAttached: false,
    idleHandle: null,
    timeoutHandle: null,
    webVitalsFlushHandle: null,
    pendingIdentifies: [],
      pendingEvents: [],
      pendingWebVitals: {},
      reportedWebVitals: [],
      webVitalsObservers: [],
      resetQueued: false,
      adminCaptureSuppressed: false,
  };

  win[POSTHOG_STATE_KEY].pendingEvents ??= [];
  win[POSTHOG_STATE_KEY].webVitalsAttached ??= false;
  win[POSTHOG_STATE_KEY].webVitalsFlushHandle ??= null;
  win[POSTHOG_STATE_KEY].pendingWebVitals ??= {};
  win[POSTHOG_STATE_KEY].reportedWebVitals ??= [];
  win[POSTHOG_STATE_KEY].webVitalsObservers ??= [];
  win[POSTHOG_STATE_KEY].adminCaptureSuppressed ??= false;

  return win[POSTHOG_STATE_KEY];
}

async function flushPendingPostHogCalls(runtimeState = getPostHogRuntimeState()) {
  if (!runtimeState.initialized) return;
  const posthog = await loadPostHogClient();

  const pendingEvents = runtimeState.pendingEvents.splice(0);
  for (const pendingEvent of pendingEvents) {
    posthog.capture(pendingEvent.eventName, pendingEvent.properties);
  }

  const pendingIdentifies = runtimeState.pendingIdentifies.splice(0);
  for (const pendingIdentify of pendingIdentifies) {
    await applyPostHogIdentify(pendingIdentify.userId, pendingIdentify.properties);
  }

  if (runtimeState.resetQueued) {
    posthog.reset();
    runtimeState.resetQueued = false;
  }
}

function queuePostHogIdentify(userId: string, properties?: Record<string, unknown>) {
  const runtimeState = getPostHogRuntimeState();
  runtimeState.pendingIdentifies.push({ userId, properties });

  if (runtimeState.pendingIdentifies.length > MAX_PENDING_IDENTIFIES) {
    runtimeState.pendingIdentifies.splice(0, runtimeState.pendingIdentifies.length - MAX_PENDING_IDENTIFIES);
  }
}

function capturePostHogEvent(
  eventName: string,
  properties?: Record<string, unknown>,
  options?: { deferInit?: boolean }
) {
  if (shouldSkipPostHog()) return;

  const runtimeState = getPostHogRuntimeState();

  // Route through posthog-js whenever it's ready: SDK captures carry $lib,
  // $session_id, user agent, and referrer, which Web Analytics and attribution
  // require. The bare /capture/ fetch produced lib-less, session-less events.
  if (runtimeState.initialized && posthogClient) {
    posthogClient.capture(eventName, properties);
    return;
  }

  // SDK not ready yet: queue and flush through the SDK right after init.
  runtimeState.pendingEvents.push({ eventName, properties });
  if (runtimeState.pendingEvents.length > MAX_PENDING_EVENTS) {
    runtimeState.pendingEvents.splice(0, runtimeState.pendingEvents.length - MAX_PENDING_EVENTS);
  }

  if (options?.deferInit) {
    schedulePostHogInit();
  } else {
    void initPostHog();
  }
}

function postHogPathnameFromPayload(event: PostHogCapturePayload) {
  const properties = event.properties ?? {};
  const pathname = properties.$pathname ?? properties.page_path;
  if (typeof pathname === "string") return pathname;

  const currentUrl = properties.$current_url ?? properties.page_location;
  if (typeof currentUrl === "string") {
    try {
      return new URL(currentUrl, window.location.origin).pathname;
    } catch {
      return currentUrl;
    }
  }

  return getCurrentPostHogPathname();
}

// Microsoft Outlook Safe Links / Office link-scanner bots execute page JS
// in a headless sandbox and throw this specific error; it is not a real
// user-facing exception. Confirmed 97% of $exception volume on the Roma
// property (2026-07-11 SEO/conversion audit) before this filter existed.
const OUTLOOK_SAFELINKS_EXCEPTION_PATTERN = /Object Not Found Matching Id/i;

function isOutlookSafeLinksNoise(event: PostHogCapturePayload) {
  if (event.event !== "$exception") return false;
  const message = event.properties?.$exception_message;
  return typeof message === "string" && OUTLOOK_SAFELINKS_EXCEPTION_PATTERN.test(message);
}

function filterAdminPostHogEvent(event: CaptureResult | null) {
  if (!event) return event;
  const payload = event as PostHogCapturePayload;
  if (isOutlookSafeLinksNoise(payload)) return null;
  return isAdminAnalyticsPath(postHogPathnameFromPayload(payload)) ? null : event;
}

function syncAdminPostHogCapture(pathname = getCurrentPostHogPathname()) {
  if (!posthogClient) return;

  const runtimeState = getPostHogRuntimeState();
  const suppressAdminCapture = isAdminAnalyticsPath(pathname);
  if (runtimeState.adminCaptureSuppressed === suppressAdminCapture) return;

  posthogClient.set_config({
    autocapture: suppressAdminCapture ? false : true,
    capture_dead_clicks: suppressAdminCapture ? false : true,
  });
  runtimeState.adminCaptureSuppressed = suppressAdminCapture;
}

function captureUnhandledError(event: ErrorEvent) {
  if (isAdminAnalyticsPath(getCurrentPostHogPathname())) return;
  if (!posthogClient) return;
  posthogClient.capture("$exception", {
    $exception_message: event.message,
    $exception_source: event.filename,
    $exception_lineno: event.lineno,
    $exception_colno: event.colno,
    $exception_type: "unhandled_error",
  });
}

function captureUnhandledRejection(event: PromiseRejectionEvent) {
  if (isAdminAnalyticsPath(getCurrentPostHogPathname())) return;
  if (!posthogClient) return;
  posthogClient.capture("$exception", {
    $exception_message: event.reason?.message || String(event.reason),
    $exception_type: "unhandled_rejection",
  });
}

function roundWebVitalValue(name: WebVitalName, value: number) {
  return name === "CLS" ? Number(value.toFixed(4)) : Math.round(value);
}

function getWebVitalRating(name: WebVitalName, value: number): WebVitalRating {
  if (name === "CLS") {
    if (value <= 0.1) return "good";
    if (value <= 0.25) return "needs-improvement";
    return "poor";
  }

  if (name === "INP") {
    if (value <= 200) return "good";
    if (value <= 500) return "needs-improvement";
    return "poor";
  }

  if (name === "LCP") {
    if (value <= 2500) return "good";
    if (value <= 4000) return "needs-improvement";
    return "poor";
  }

  if (value <= 1800) return "good";
  if (value <= 3000) return "needs-improvement";
  return "poor";
}

function getNavigationType() {
  const navigationEntry = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
  return navigationEntry?.type || "navigate";
}

function scheduleProductionWebVitalsFlush() {
  const runtimeState = getPostHogRuntimeState();
  if (runtimeState.webVitalsFlushHandle) return;

  runtimeState.webVitalsFlushHandle = setTimeout(() => {
    runtimeState.webVitalsFlushHandle = null;
    flushProductionWebVitals("delayed_flush");
  }, WEB_VITALS_FLUSH_DELAY_MS);
}

function recordProductionWebVital(
  name: WebVitalName,
  value: number,
  attribution?: Record<string, string | number | boolean | null>
) {
  if (!Number.isFinite(value) || !shouldCaptureProductionWebVitals()) return;

  const runtimeState = getPostHogRuntimeState();
  if (runtimeState.reportedWebVitals.includes(name)) return;

  const roundedValue = roundWebVitalValue(name, value);
  runtimeState.pendingWebVitals[name] = {
    name,
    value: roundedValue,
    rating: getWebVitalRating(name, roundedValue),
    id: `${webVitalsPageId}_${name.toLowerCase()}`,
    delta: roundedValue,
    navigationType: getNavigationType(),
    source: "direct_performance_observer",
    timestamp: Date.now(),
    attribution,
  };

  scheduleProductionWebVitalsFlush();
}

function flushProductionWebVitals(reason: string) {
  if (!shouldCaptureProductionWebVitals()) return;

  const runtimeState = getPostHogRuntimeState();
  if (runtimeState.webVitalsFlushHandle) {
    clearTimeout(runtimeState.webVitalsFlushHandle);
    runtimeState.webVitalsFlushHandle = null;
  }

  const metrics = WEB_VITAL_NAMES.flatMap((name) => {
    const metric = runtimeState.pendingWebVitals[name];
    return metric && !runtimeState.reportedWebVitals.includes(name) ? [metric] : [];
  });

  if (metrics.length === 0) return;

  const properties: Record<string, unknown> = {
    $web_vitals_source: "direct_performance_observer",
    $web_vitals_reason: reason,
    $web_vitals_metric_names: metrics.map((metric) => metric.name),
    $current_url: window.location.href,
    $host: window.location.host,
    $pathname: window.location.pathname,
  };

  for (const metric of metrics) {
    properties[`$web_vitals_${metric.name}_event`] = {
      ...metric,
      $current_url: window.location.href,
      $host: window.location.host,
      $pathname: window.location.pathname,
    };
    properties[`$web_vitals_${metric.name}_value`] = metric.value;
    runtimeState.reportedWebVitals.push(metric.name);
    delete runtimeState.pendingWebVitals[metric.name];
  }

  sendDirectPostHogCapture("$web_vitals", properties);
}

function observeProductionWebVitalEntries(
  type: string,
  callback: PerformanceObserverCallback,
  options?: Record<string, unknown>
) {
  try {
    const observer = new PerformanceObserver(callback);
    observer.observe({ type, buffered: true, ...options } as PerformanceObserverInit);
    getPostHogRuntimeState().webVitalsObservers.push(observer);
    return true;
  } catch {
    return false;
  }
}

function attachProductionWebVitalsCapture() {
  if (!shouldCaptureProductionWebVitals() || typeof PerformanceObserver === "undefined") return;

  const runtimeState = getPostHogRuntimeState();
  if (runtimeState.webVitalsAttached) return;
  runtimeState.webVitalsAttached = true;

  let cumulativeLayoutShift = 0;
  let longestInteraction = 0;

  recordProductionWebVital("CLS", 0, { observed_entries: 0 });

  observeProductionWebVitalEntries("paint", (list) => {
    for (const entry of list.getEntries()) {
      if (entry.name === "first-contentful-paint") {
        recordProductionWebVital("FCP", entry.startTime, { entry_type: entry.entryType });
      }
    }
  });

  observeProductionWebVitalEntries("largest-contentful-paint", (list) => {
    for (const entry of list.getEntries() as LargestContentfulPaintEntry[]) {
      const value = entry.renderTime || entry.loadTime || entry.startTime;
      recordProductionWebVital("LCP", value, {
        entry_type: entry.entryType,
        size: entry.size ?? null,
        element: entry.element?.tagName?.toLowerCase() ?? null,
      });
    }
  });

  observeProductionWebVitalEntries("layout-shift", (list) => {
    for (const entry of list.getEntries() as LayoutShiftEntry[]) {
      if (entry.hadRecentInput) continue;
      cumulativeLayoutShift += entry.value || 0;
    }

    recordProductionWebVital("CLS", cumulativeLayoutShift, { observed_entries: list.getEntries().length });
  });

  observeProductionWebVitalEntries(
    "event",
    (list) => {
      for (const entry of list.getEntries() as EventTimingEntry[]) {
        if (!["click", "keydown", "mousedown", "pointerdown", "pointerup", "touchend"].includes(entry.name)) {
          continue;
        }

        if (entry.duration <= longestInteraction) continue;
        longestInteraction = entry.duration;
        recordProductionWebVital("INP", entry.duration, {
          entry_type: entry.entryType,
          event_type: entry.name,
          interaction_id: entry.interactionId || null,
          processing_time: Math.max(0, (entry.processingEnd || 0) - (entry.processingStart || 0)),
          start_time: Math.round(entry.startTime),
        });
      }
    },
    { durationThreshold: 16 }
  );

  const flushOnHidden = () => {
    if (document.visibilityState === "hidden") {
      flushProductionWebVitals("visibility_hidden");
    }
  };

  document.addEventListener("visibilitychange", flushOnHidden, { passive: true });
  window.addEventListener("pagehide", () => flushProductionWebVitals("pagehide"), { capture: true });
}

// Defer PostHog briefly so the first paint stays clean, but do not wait long
// enough for normal restaurant visitors to bounce before the pageview flushes.
async function initPostHog() {
  const brandConfig = resolveAnalyticsBrandConfig();
  if (!brandConfig?.posthogKey || !shouldCaptureAnalytics(brandConfig.hostname)) return;
  if (isAdminAnalyticsPath(getCurrentPostHogPathname())) return;

  activeBrandConfig = brandConfig;
  const posthogKey = brandConfig.posthogKey;

  const runtimeState = getPostHogRuntimeState();
  if (runtimeState.initialized || runtimeState.initializing) return;
  runtimeState.initializing = true;

  try {
    const posthog = await loadPostHogClient();

    posthog.init(posthogKey, {
      api_host: POSTHOG_HOST,
      ui_host: POSTHOG_UI_HOST,
      person_profiles: "identified_only",
      capture_pageview: false, // We handle this manually for better control
      capture_pageleave: true,
      capture_performance: false, // Web vitals are captured via direct /capture/ for production data quality.
      autocapture: true,
      // Capture dead clicks and heatmap data to find broken or confusing UI.
      capture_dead_clicks: true,
      capture_heatmaps: true,
      // Feature flags - enabled for A/B testing
      advanced_disable_feature_flags: false,
      advanced_disable_decide: false,
      // Public restaurant pages do not need session replay. Disabling it stops
      // posthog-js from remotely fetching the ~152KB recorder bundle on every
      // visit, which competes with LCP on mobile. Keep autocapture + heatmaps.
      disable_session_recording: true,
      // No survey is defined anywhere in this codebase (grep for survey usage
      // returns zero results), so prevent posthog-js from remotely loading the
      // ~31KB surveys bundle and polling for active surveys.
      disable_surveys: true,
      // Batch requests for better performance
      request_batching: true,
      before_send: filterAdminPostHogEvent,
      bootstrap: {
        distinctID: getPostHogDistinctId(),
      },
    });

    // Super properties - attached to EVERY event automatically
    posthog.register({
      ...getAnalyticsBrandProperties(brandConfig),
      platform: "web",
      environment: getPostHogRuntimeEnvironment(),
    });

    // One-time properties - only set on first visit
    posthog.register_once({
      first_visit_date: new Date().toISOString(),
      initial_referrer: document.referrer || "direct",
      initial_landing_page: window.location.pathname,
    });

    if (!runtimeState.listenersAttached) {
      window.addEventListener("error", captureUnhandledError);
      window.addEventListener("unhandledrejection", captureUnhandledRejection);
      runtimeState.listenersAttached = true;
    }

    runtimeState.initialized = true;
    syncAdminPostHogCapture();
    await flushPendingPostHogCalls(runtimeState);
  } catch (error) {
    runtimeState.initialized = false;
    // Last-resort delivery so queued events aren't lost when the SDK cannot
    // initialize; these arrive lib-less but are better than nothing.
    const pendingEvents = runtimeState.pendingEvents.splice(0);
    for (const pendingEvent of pendingEvents) {
      sendDirectPostHogCapture(pendingEvent.eventName, pendingEvent.properties);
    }
    console.warn('[PostHog] Initialization error:', error);
  } finally {
    runtimeState.initializing = false;
  }
}

function schedulePostHogInit() {
  if (shouldSkipPostHog()) return;

  const runtimeState = getPostHogRuntimeState();
  if (runtimeState.initialized || runtimeState.idleHandle || runtimeState.timeoutHandle) {
    return;
  }

  const initDelay = isStaticCompliancePath(getCurrentPostHogPathname())
    ? STATIC_COMPLIANCE_INIT_DELAY_MS
    : PASSIVE_INIT_DELAY_MS;

  runtimeState.timeoutHandle = setTimeout(() => {
    runtimeState.timeoutHandle = null;

    if ("requestIdleCallback" in window) {
      runtimeState.idleHandle = window.requestIdleCallback(
        () => {
          runtimeState.idleHandle = null;
          void initPostHog();
        },
        { timeout: PASSIVE_INIT_IDLE_TIMEOUT_MS }
      );
    } else {
      void initPostHog();
    }
  }, initDelay);
}

function cancelScheduledPostHogInit() {
  if (typeof window === "undefined") return;

  const runtimeState = getPostHogRuntimeState();
  if (runtimeState.idleHandle && "cancelIdleCallback" in window) {
    window.cancelIdleCallback(runtimeState.idleHandle);
    runtimeState.idleHandle = null;
  }
  if (runtimeState.timeoutHandle) {
    clearTimeout(runtimeState.timeoutHandle);
    runtimeState.timeoutHandle = null;
  }
}

// Page view tracker component
function PostHogPageView() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    syncAdminPostHogCapture(pathname);

    if (pathname && !shouldSkipPostHog() && !isAdminAnalyticsPath(pathname)) {
      let url = window.origin + pathname;
      if (searchParams?.toString()) {
        url = url + `?${searchParams.toString()}`;
      }
      capturePostHogEvent("$pageview", {
        $current_url: url,
        $pathname: pathname,
        ...getPageviewAttributionProperties(),
      }, { deferInit: true });
    }
  }, [pathname, searchParams]);

  return null;
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    if (shouldSkipPostHog()) return;
    schedulePostHogInit();
    attachProductionWebVitalsCapture();
    return cancelScheduledPostHogInit;
  }, []);

  // Hostname detection is browser-only. Always render the same provider tree
  // during SSR and the first client pass; PostHogPageView and the initialization
  // effect already enforce the key and production-host capture guards.
  return (
    <>
      <Suspense fallback={null}>
        <PostHogPageView />
      </Suspense>
      {children}
    </>
  );
}

const ATTRIBUTION_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "msclkid",
  "ttclid",
  "twclid",
  "li_fat_id",
] as const;

// Helper to get UTM parameters - checks URL first, then sessionStorage
function getUtmParams(): Record<string, string> {
  if (typeof window === "undefined") return {};

  const brandConfig = getActiveBrandConfig() ?? resolveAnalyticsBrandConfig();
  if (!brandConfig) return {};

  const params = new URLSearchParams(window.location.search);
  const utmParams: Record<string, string> = {};

  let hasNewUtmParams = false;
  ATTRIBUTION_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) {
      utmParams[key] = value;
      hasNewUtmParams = true;
    }
  });

  if (hasNewUtmParams) {
    writeBrandUtmStorage(brandConfig, utmParams);
    return utmParams;
  }

  try {
    const stored = readBrandUtmStorage(brandConfig);
    if (stored) {
      return JSON.parse(stored) as Record<string, string>;
    }
  } catch {
    // sessionStorage not available or invalid JSON
  }

  return utmParams;
}

function sanitizePostHogUrl(url: string) {
  if (!url) return undefined;

  try {
    const parsedUrl = new URL(url, window.location.origin);
    return `${parsedUrl.origin}${parsedUrl.pathname}`;
  } catch {
    return undefined;
  }
}

function getReferrerHost(referrer: string | undefined) {
  if (!referrer || referrer === "direct") return undefined;

  try {
    return new URL(referrer).hostname.replace(/^www\./, "");
  } catch {
    return undefined;
  }
}

function inferTrafficSource(params: Record<string, string>, referrerHost?: string) {
  if (params.utm_source) return params.utm_source;
  if (params.gclid || params.gbraid || params.wbraid) return "google";
  if (params.fbclid) return "facebook";
  if (params.msclkid) return "bing";
  if (params.ttclid) return "tiktok";
  if (params.twclid) return "x";
  if (params.li_fat_id) return "linkedin";
  if (!referrerHost) return "direct";

  if (/google\./i.test(referrerHost)) return "google";
  if (/bing\./i.test(referrerHost)) return "bing";
  if (/yahoo\./i.test(referrerHost)) return "yahoo";
  if (/duckduckgo\./i.test(referrerHost)) return "duckduckgo";
  if (/facebook\.|instagram\.|l\.facebook/i.test(referrerHost)) return "meta";
  if (/tiktok\./i.test(referrerHost)) return "tiktok";
  if (/linkedin\./i.test(referrerHost)) return "linkedin";

  return referrerHost;
}

function inferTrafficMedium(params: Record<string, string>, referrerHost?: string) {
  if (params.utm_medium) return params.utm_medium;
  if (params.gclid || params.gbraid || params.wbraid || params.fbclid || params.msclkid) {
    return "paid";
  }
  if (!referrerHost) return "direct";
  if (/google\.|bing\.|yahoo\.|duckduckgo\./i.test(referrerHost)) return "organic";
  if (/facebook\.|instagram\.|l\.facebook|tiktok\.|linkedin\./i.test(referrerHost)) {
    return "social_referral";
  }
  return "referral";
}

function getPageviewAttributionProperties(): Record<string, string> {
  if (typeof window === "undefined") return {};

  const params = getUtmParams();
  const referrer = document.referrer ? (sanitizePostHogUrl(document.referrer) ?? "direct") : "direct";
  const referrerHost = getReferrerHost(referrer);
  const trafficSource = inferTrafficSource(params, referrerHost);
  const trafficMedium = inferTrafficMedium(params, referrerHost);
  const properties: Record<string, string> = {
    ...params,
    referrer,
    traffic_source: trafficSource,
    traffic_medium: trafficMedium,
    trafficSource,
    trafficMedium,
  };

  if (referrerHost) {
    properties.referrer_host = referrerHost;
    properties.$referring_domain = referrerHost;
  }

  if (referrer !== "direct") {
    properties.$referrer = referrer;
  }

  return properties;
}

// Helper to get browser/device context
function getBrowserContext(): Record<string, string | boolean> {
  if (typeof window === "undefined") return {};

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  return {
    referrer: document.referrer || "direct",
    current_page: window.location.pathname,
    is_mobile: isMobile,
    screen_width: window.screen.width.toString(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}

async function applyPostHogIdentify(userId: string, properties?: Record<string, unknown>) {
  const posthog = await loadPostHogClient();

  // Merge user properties with UTM params and browser context
  const enrichedProperties = {
    ...properties,
    ...getUtmParams(),
    ...getBrowserContext(),
    identified_at: new Date().toISOString(),
  };

  posthog.identify(userId, enrichedProperties);

  // Set persistent person properties for customer insights
  if (properties?.email) {
    posthog.people.set({
      email: properties.email,
      name: properties.name || properties.firstName,
      last_seen: new Date().toISOString(),
    });
  }

  // Set first-time only properties
  posthog.people.set_once({
    first_identified: new Date().toISOString(),
    signup_source: getUtmParams().utm_source || "organic",
  });
}

// Helper to identify users (call after authentication)
export function identifyUser(userId: string, properties?: Record<string, unknown>) {
  if (shouldSkipPostHog()) return;

  const runtimeState = getPostHogRuntimeState();
  if (!runtimeState.initialized) {
    queuePostHogIdentify(userId, properties);
    void initPostHog();
    return;
  }

  void applyPostHogIdentify(userId, properties);
}

// Track customer actions with value (for LTV calculation)
export function trackCustomerAction(
  action: "reservation" | "order" | "event_rsvp" | "inquiry",
  value?: number,
  metadata?: Record<string, unknown>
) {
  if (!shouldSkipPostHog()) {
    // Capture the action event with value for revenue tracking
    capturePostHogEvent(`customer_${action}`, {
      action_type: action,
      value: value || 0,
      currency: "USD",
      ...metadata,
      // Special properties for PostHog revenue tracking
      $set: {
        last_action: action,
        last_action_date: new Date().toISOString(),
        last_action_value: value || 0,
      },
    });
  }
}

// Helper to reset user (call on logout)
export function resetUser() {
  if (shouldSkipPostHog()) return;

  const runtimeState = getPostHogRuntimeState();
  runtimeState.pendingIdentifies = [];

  if (!runtimeState.initialized) {
    runtimeState.resetQueued = true;
    schedulePostHogInit();
    return;
  }

  void loadPostHogClient().then((posthog) => {
    posthog.reset();
  });
}

// Helper to track custom events
export function trackPostHogEvent(eventName: string, properties?: Record<string, unknown>) {
  capturePostHogEvent(eventName, properties);
}

// Export feature flag hooks for A/B testing
export function useFeatureFlagEnabled() {
  return false;
}

export function useFeatureFlagVariantKey() {
  return undefined;
}
